import { useEffect, useId, useRef, useState } from 'react';
import { allowanceMinutes, minutesUsedThisPeriod, walletView } from '@acs/shared';
import { Icon, Skeleton } from '@acs/ui';
import type { IconName } from '@acs/ui';
import { useI18n } from '../../i18n/index.js';
import type { TranslationKey } from '../../i18n/index.js';
import type { BusinessDoc } from '../../shared/api/converters.js';
import { useDashboardStats } from './hooks.js';

/**
 * The day's numbers as one line of badges — icon and figure, nothing else.
 *
 * Two screens mount it. On a phone it REPLACES the dashboard's five KPI tiles:
 * stacked, those tiles are most of a screen of chrome for five small integers,
 * and they pushed the thing an owner actually opens the app for — today's
 * schedule — below the fold. On the businesses list it is the whole point of the
 * card: "is this one working?" answered without opening it.
 *
 * The figures come from the SAME hook the KPI strip uses, deliberately. An owner
 * must never be able to read two different answers to "how many calls today" on
 * two screens of one product, and the cheapest way to guarantee that is for both
 * to be the same arithmetic over the same query.
 *
 * ── Why the description is a tap, not a label ────────────────────────────────
 * A badge is a number and a symbol; on its own it is not self-explanatory, and
 * five labelled badges would just be the KPI strip again in a smaller font.
 * Tapping one says what it counts. That is a tooltip on hover for a mouse, but
 * a phone has no hover — so it is opened by CLICK here, one at a time, and the
 * panel is laid out across the whole row rather than floating over the badge,
 * because a tip anchored to the last badge on a 360px screen hangs off the edge.
 */

interface BadgeSpec {
  key: string;
  icon: IconName;
  value: string | number;
  /** What it counts, in two or three words — the tip's heading. */
  labelKey: TranslationKey;
  /** One sentence. Why the number is worth looking at. */
  descriptionKey: TranslationKey;
  /** `warning`/`danger` tint the badge itself; used for minutes running out and
   *  for missed calls, which are the two numbers that mean "do something". */
  tone?: 'neutral' | 'warning' | 'danger';
}

export function StatBadges({ business, className }: { business: BusinessDoc; className?: string }) {
  const { t } = useI18n();
  const { stats, isLoading } = useDashboardStats(business.id, business.timezone);

  const [open, setOpen] = useState<string | null>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const tipId = useId();

  // Close on a click anywhere else and on Escape. Registered only while a tip is
  // open, so the common case costs no document listeners.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent | TouchEvent) => {
      if (!rowRef.current?.contains(e.target as Node)) setOpen(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(null);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (isLoading || !stats) {
    return (
      <div className={`stat-badges stat-badges--loading${className ? ` ${className}` : ''}`}>
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} height={30} width={62} radius="var(--tc-radius-pill)" />
        ))}
      </div>
    );
  }

  const allowance = allowanceMinutes(business);
  // Re-applies the writer's lazy period reset, so a counter whose window has
  // closed reads 0 here too instead of last month's total — same call the KPI
  // tile makes, for the same reason.
  const used = minutesUsedThisPeriod(business, new Date());
  const usedPct = allowance === null ? 0 : Math.round((used / allowance) * 100);
  // Prepaid has no fixed allowance (see MinutesCard): show what new calls
  // can still use, not `used/<plan tier>`.
  const available =
    business.billingSummary.mode === 'prepaid_v1'
      ? Math.floor(walletView(business.billingSummary, new Date()).availableSeconds / 60)
      : null;

  const badges: BadgeSpec[] = [
    available !== null
      ? {
          key: 'minutes',
          icon: 'clock',
          value: available,
          labelKey: 'dashboard.kpi.minutesAvailable',
          descriptionKey: 'dashboard.badge.minutesAvailable',
          tone: available <= 0 ? 'danger' : 'neutral',
        }
      : {
          key: 'minutes',
          icon: 'clock',
          value: allowance === null ? used : `${used}/${allowance}`,
          labelKey: 'dashboard.kpi.minutes',
          descriptionKey: allowance === null ? 'dashboard.badge.minutesNoPlan' : 'dashboard.badge.minutes',
          tone: allowance === null ? 'neutral' : usedPct >= 80 ? 'danger' : usedPct >= 50 ? 'warning' : 'neutral',
        },
    {
      key: 'calls',
      icon: 'phone-incoming',
      value: stats.callsToday,
      labelKey: 'dashboard.kpi.callsAnswered',
      descriptionKey: 'dashboard.badge.calls',
    },
    {
      key: 'bookings',
      icon: 'calendar',
      value: stats.bookingsToday,
      labelKey: 'dashboard.kpi.bookings',
      descriptionKey: 'dashboard.badge.bookings',
    },
    {
      key: 'missed',
      icon: 'phone-missed',
      value: stats.missedToday,
      labelKey: 'dashboard.kpi.missed',
      descriptionKey: 'dashboard.badge.missed',
      // Only a tint when there is something to tint. A permanent red 0 is a
      // warning about nothing, which is how people learn to ignore the colour.
      tone: stats.missedToday > 0 ? 'warning' : 'neutral',
    },
    {
      key: 'volume',
      icon: 'phone',
      value: stats.volume.reduce((n, b) => n + b.count, 0),
      labelKey: 'dashboard.kpi.volume',
      descriptionKey: 'dashboard.badge.volume',
    },
  ];

  const openBadge = badges.find((b) => b.key === open) ?? null;

  return (
    <div className={`stat-badges${className ? ` ${className}` : ''}`} ref={rowRef}>
      {badges.map((badge) => (
        <button
          key={badge.key}
          type="button"
          className={`stat-badge${badge.tone && badge.tone !== 'neutral' ? ` stat-badge--${badge.tone}` : ''}${
            open === badge.key ? ' is-open' : ''
          }`}
          aria-label={`${t(badge.labelKey)}: ${badge.value}`}
          aria-expanded={open === badge.key}
          aria-describedby={open === badge.key ? tipId : undefined}
          onClick={() => setOpen((prev) => (prev === badge.key ? null : badge.key))}
        >
          <Icon name={badge.icon} size={15} />
          <span className="stat-badge__value">{badge.value}</span>
        </button>
      ))}

      {openBadge && (
        <span role="tooltip" id={tipId} className="stat-badge__tip">
          <strong>{t(openBadge.labelKey)}</strong> {t(openBadge.descriptionKey)}
        </span>
      )}
    </div>
  );
}
